import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Save, Send, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import MailPostausgang from './MailPostausgang';

const FELD = {
  width: '100%',
  border: '1px solid #E2E8F0',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 13,
  color: '#0F172A',
  background: '#fff',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const BESCHRIFTUNG = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#334155',
  marginBottom: 5,
};

const HINWEIS = { fontSize: 11, color: '#94A3B8', margin: '4px 0 0' };

// Je Art: Webhook-Weg, Einstellungs-Weg und was zur Bitrix-Regel zu sagen ist
// (Logik und Mailtext: api/_lib/mailAuftrag.js und die Datei der Art).
const ARTEN = {
  'recherche-auftrag': {
    einstellungen: 'recherche-einstellungen',
    regelHinweis: 'Die Mail geht bei jedem Aufruf raus (seit 08.10.2026) – welche Deals den Auftrag bekommen, entscheidet die Phase, an der die Regel steht. Fehlt Amtsgericht, Gemeinde oder Netzbetreiber, steht das im Betreff.',
    zeitleiste: 'Die versandte Mail kommt nicht in die Zeitleiste des Deals – dort steht nur etwas, wenn der Versand endgültig scheitert oder gesperrt war.',
    // Seit 08.10.2026 aenderbar (vorher fester Satz plus Recherche-Seiten).
    auftragstextHinweis: 'Steht nach der Anrede; #DEAL_ID# wird zur Deal-Nummer. Darunter folgt von selbst der Link zum Deal – '
      + 'keine Namen, Anschrift oder Flurstück in den Text schreiben, die Mail geht nach außen. Leer speichern stellt die Vorgabe wieder her.',
    auftragstextZeilen: 14,
  },
  'flaechenpruefung-auftrag': {
    einstellungen: 'flaechenpruefung-einstellungen',
    regelHinweis: 'Die Mail geht bei jedem Aufruf raus – welche Deals eine Flächenprüfung brauchen, entscheidet die Phase, an der die Regel steht. '
      + 'Ohne Anhänge (seit 08.10.2026): Prüfanleitung und Prüfmappen-Vorlage liegen bei Fiona.',
    zeitleiste: 'Im Deal steht nach dem Versand nur der Vermerk „Flächenprüfung angefordert.“ – ohne Empfänger und ohne Mailinhalt. '
      + 'Ausführlicher wird es nur, wenn der Versand endgültig scheitert oder gesperrt war.',
    // Seit 08.10.2026 ist der Auftragstext die ganze Mail (vorher folgten Angaben aus dem Deal).
    auftragstextHinweis: 'Steht nach der Anrede; #DEAL_ID# wird zur Deal-Nummer, *Text* erscheint fett. Darunter folgt von selbst '
      + 'der Link zum Deal – keine Namen, Anschrift oder Flurstück in den Text schreiben, die Mail geht nach außen. '
      + 'Leer speichern stellt die Vorgabe wieder her.',
    auftragstextZeilen: 14,
  },
};

function Knopf({ children, onClick, disabled, variante = 'schlicht', icon: Icon }) {
  const haupt = variante === 'haupt';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        background: haupt ? '#E07000' : '#fff',
        color: haupt ? '#fff' : '#334155',
        border: haupt ? '1px solid #E07000' : '1px solid #E2E8F0',
        borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
      }}
    >
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
}

/**
 * Mail-Auftraege (seit 02.10.2026): wer die Mail bekommt, von welchem Postfach, mit welcher
 * Anrede und - wo die Art es anbietet - mit welchem Auftragstext; dazu eine Testmail und die
 * Anleitung fuer die Bitrix-Regel.
 */
export default function MailAuftragEinstellungen({ apiBase, session, onGespeichert, art }) {
  const {
    einstellungen: einstellungsPfad, regelHinweis, zeitleiste, auftragstextHinweis, auftragstextZeilen,
  } = ARTEN[art] || ARTEN['recherche-auftrag'];
  const [stand, setStand] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [testet, setTestet] = useState(false);
  const [fehler, setFehler] = useState('');

  const [empfaenger, setEmpfaenger] = useState('');
  const [absender, setAbsender] = useState('');
  const [anrede, setAnrede] = useState('');
  const [auftragstext, setAuftragstext] = useState('');
  const [testEmpfaenger, setTestEmpfaenger] = useState('');
  const [testDeal, setTestDeal] = useState('');

  const authHeaders = useMemo(() => (
    session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
  ), [session]);

  const uebernehmen = useCallback((daten) => {
    setStand(daten);
    setEmpfaenger((daten?.einstellungen?.empfaenger || []).join(', '));
    setAbsender(daten?.einstellungen?.absender || '');
    setAnrede(daten?.einstellungen?.anrede || '');
    setAuftragstext(daten?.einstellungen?.auftragstext || '');
  }, []);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      const antwort = await axios.get(`${apiBase}/automation/${einstellungsPfad}`, { headers: authHeaders });
      uebernehmen(antwort.data);
    } catch (error) {
      setFehler(error?.response?.data?.detail || error.message || 'Einstellungen konnten nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [apiBase, authHeaders, einstellungsPfad, uebernehmen]);

  useEffect(() => { laden(); }, [laden]);

  // Nur den Postausgang erneuern - die Formularfelder (evtl. ungespeichert) bleiben, wie sie sind.
  const postausgangUebernehmen = useCallback((postausgang) => {
    setStand((vorher) => (vorher ? { ...vorher, postausgang } : vorher));
  }, []);

  useEffect(() => {
    if (!testEmpfaenger && session?.user?.email) setTestEmpfaenger(session.user.email);
  }, [session, testEmpfaenger]);

  const mitAuftragstext = stand?.mitAuftragstext === true;

  const speichern = async () => {
    setSpeichert(true);
    try {
      const antwort = await axios.put(`${apiBase}/automation/${einstellungsPfad}`, {
        empfaenger,
        absender,
        anrede,
        ...(mitAuftragstext ? { auftragstext } : {}),
      }, { headers: authHeaders });
      uebernehmen(antwort.data);
      toast.success('Gespeichert.');
      if (onGespeichert) onGespeichert();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  };

  const testen = async () => {
    setTestet(true);
    try {
      const antwort = await axios.post(`${apiBase}/automation/${einstellungsPfad}`, {
        aktion: 'test',
        testEmpfaenger,
        dealId: testDeal.trim(),
      }, { headers: authHeaders, timeout: 60000 });
      toast.success(antwort.data?.message || 'Testmail versendet.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Testmail fehlgeschlagen.');
    } finally {
      setTestet(false);
    }
  };

  const einsatzbereit = stand?.einsatzbereit === true;
  const webhookAdresse = `${window.location.origin}/api/automation/${art}`;

  if (laedt) {
    return <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Lädt…</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {fehler && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
          {fehler}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 600,
        color: einsatzbereit ? '#15803D' : '#B45309',
      }}>
        {einsatzbereit ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        {einsatzbereit
          ? 'Alles hinterlegt – die Mail kann versendet werden.'
          : `Noch nicht einsatzbereit: ${(stand?.fehlt || []).join(', ') || 'Angaben fehlen'}.`}
      </div>

      <MailPostausgang
        apiBase={apiBase}
        authHeaders={authHeaders}
        einstellungsPfad={einstellungsPfad}
        postausgang={stand?.postausgang || null}
        onPostausgang={postausgangUebernehmen}
      />

      <div>
        <label style={BESCHRIFTUNG} htmlFor={`${art}-empfaenger`}>Empfänger</label>
        <input
          id={`${art}-empfaenger`}
          type="text"
          value={empfaenger}
          onChange={(e) => setEmpfaenger(e.target.value)}
          placeholder="name@beispiel.de, zweite@beispiel.de"
          style={FELD}
        />
        <p style={HINWEIS}>Eine oder mehrere Adressen, mit Komma getrennt – auch außerhalb von Bitrix.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div>
          <label style={BESCHRIFTUNG} htmlFor={`${art}-absender`}>Absender</label>
          <select
            id={`${art}-absender`}
            value={absender}
            onChange={(e) => setAbsender(e.target.value)}
            style={FELD}
          >
            {(stand?.absenderListe || []).map((eintrag) => (
              <option key={eintrag.email} value={eintrag.email}>{eintrag.label} &lt;{eintrag.email}&gt;</option>
            ))}
          </select>
        </div>
        <div>
          <label style={BESCHRIFTUNG} htmlFor={`${art}-anrede`}>Anrede</label>
          <input
            id={`${art}-anrede`}
            type="text"
            value={anrede}
            onChange={(e) => setAnrede(e.target.value)}
            placeholder="Hallo,"
            style={FELD}
          />
        </div>
      </div>

      {mitAuftragstext && (
        <div>
          <label style={BESCHRIFTUNG} htmlFor={`${art}-auftragstext`}>Auftragstext</label>
          <textarea
            id={`${art}-auftragstext`}
            value={auftragstext}
            onChange={(e) => setAuftragstext(e.target.value)}
            rows={auftragstextZeilen || 4}
            style={{ ...FELD, resize: 'vertical', lineHeight: 1.5 }}
          />
          <p style={HINWEIS}>{auftragstextHinweis}</p>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        <Knopf onClick={speichern} disabled={speichert} variante="haupt" icon={Save}>
          {speichert ? 'Speichert…' : 'Speichern'}
        </Knopf>

        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={BESCHRIFTUNG} htmlFor={`${art}-test`}>Testmail an</label>
          <input
            id={`${art}-test`}
            type="email"
            value={testEmpfaenger}
            onChange={(e) => setTestEmpfaenger(e.target.value)}
            style={FELD}
          />
        </div>

        <div style={{ width: 150 }}>
          <label style={BESCHRIFTUNG} htmlFor={`${art}-test-deal`}>Deal-ID (optional)</label>
          <input
            id={`${art}-test-deal`}
            type="text"
            value={testDeal}
            onChange={(e) => setTestDeal(e.target.value)}
            placeholder="z. B. 11698"
            style={FELD}
          />
        </div>

        <Knopf onClick={testen} disabled={testet || !testEmpfaenger} icon={Send}>
          {testet ? 'Sendet…' : 'Testmail senden'}
        </Knopf>
      </div>

      <p style={{ ...HINWEIS, marginTop: -4 }}>
        Die Testmail nimmt denselben Weg wie der Ernstfall, trägt „[Test]“ im Betreff, geht nur an
        die Testadresse und hinterlässt keinen Vermerk im Deal. Mit Deal-ID enthält sie die echten
        Angaben dieses Deals, sonst Beispieldaten. Sie nutzt die gespeicherten Einstellungen –
        Änderungen also erst speichern.
      </p>

      <div style={{
        background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '14px 16px',
        display: 'grid', gap: 8, fontSize: 12, color: '#334155', lineHeight: 1.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <Webhook size={13} />
          In Bitrix einrichten
        </div>
        <div>
          An der gewünschten Phase eine Regel <strong>„Webhook“</strong> anlegen (statt „E-Mail-Nachricht“).
          Als Adresse dieselbe wie bei der Vermittler-Einladung, nur mit <code>{art}</code> statt
          <code> vermittler-einladung</code> – also mit demselben Kennwort:
        </div>
        <code style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, padding: '6px 8px', wordBreak: 'break-all' }}>
          {webhookAdresse}?secret=…
        </code>
        <div>
          {regelHinweis} {zeitleiste}
        </div>
      </div>

      {stand?.einstellungen?.aktualisiertAm && (
        <p style={HINWEIS}>
          Zuletzt geändert am {new Date(stand.einstellungen.aktualisiertAm).toLocaleString('de-DE')}
          {stand.einstellungen.aktualisiertVon ? ` von ${stand.einstellungen.aktualisiertVon}` : ''}.
        </p>
      )}
    </div>
  );
}
